import type { DashboardPeriodKind } from "./types";
import { isInclusiveISODateRange } from "@/lib/dates";

export const DASHBOARD_HISTORY_OPTIONS = [4, 8, 12] as const;
export type DashboardHistory = (typeof DASHBOARD_HISTORY_OPTIONS)[number];

export interface DashboardHistoryOption {
  requestHistory: DashboardHistory;
  displayCount: number;
  label: string;
}

export const DASHBOARD_PERIOD_OPTIONS = [
  { period: "today", label: "Hoje", compactLabel: "Hoje" },
  { period: "week", label: "Semana", compactLabel: "Semana" },
  { period: "month", label: "Mês", compactLabel: "Mês" },
  {
    period: "custom",
    label: "Personalizado",
    compactLabel: "Datas",
  },
  {
    period: "all",
    label: "Todo período",
    compactLabel: "Todo período",
  },
] as const satisfies readonly {
  period: DashboardPeriodKind;
  label: string;
  compactLabel: string;
}[];

const dashboardPeriods = new Set<DashboardPeriodKind>(
  DASHBOARD_PERIOD_OPTIONS.map(({ period }) => period),
);

export interface DashboardPeriodState {
  period: DashboardPeriodKind;
  from?: string;
  to?: string;
  history: DashboardHistory;
}

export interface DashboardResolvedPeriodRange {
  from: string;
  to: string;
}

export const DEFAULT_DASHBOARD_PERIOD_STATE = Object.freeze({
  period: "month",
  history: 4,
}) satisfies Readonly<DashboardPeriodState>;

const isDashboardHistory = (value: unknown): value is DashboardHistory =>
  DASHBOARD_HISTORY_OPTIONS.some((option) => option === value);

const assertDashboardPeriod = (
  period: DashboardPeriodKind,
): DashboardPeriodKind => {
  if (!dashboardPeriods.has(period)) {
    throw new TypeError(`unsupported dashboard period: ${period}`);
  }
  return period;
};

const assertDashboardHistory = (
  history: DashboardHistory,
): DashboardHistory => {
  if (!isDashboardHistory(history)) {
    throw new TypeError("dashboard history must be 4, 8, or 12");
  }
  return history;
};

export function createDashboardPeriodState(
  input: Partial<DashboardPeriodState> = {},
): DashboardPeriodState {
  const period = assertDashboardPeriod(input.period ?? "month");
  const history = assertDashboardHistory(input.history ?? 4);
  const state: DashboardPeriodState = { period, history };

  if (input.from !== undefined) state.from = input.from;
  if (input.to !== undefined) state.to = input.to;
  return state;
}

export function dashboardPeriodLabel(
  period: DashboardPeriodKind,
  compact = false,
): string {
  const validPeriod = assertDashboardPeriod(period);
  const option = DASHBOARD_PERIOD_OPTIONS.find(
    (candidate) => candidate.period === validPeriod,
  );
  if (!option) throw new TypeError(`unsupported dashboard period: ${period}`);
  return compact ? option.compactLabel : option.label;
}

export function dashboardHistoryOptions(
  period: DashboardPeriodKind,
  availableHistory?: number,
): readonly DashboardHistoryOption[] {
  const validPeriod = assertDashboardPeriod(period);
  if (
    availableHistory !== undefined &&
    (!Number.isSafeInteger(availableHistory) || availableHistory < 0)
  ) {
    throw new TypeError(
      "available dashboard history must be a nonnegative integer",
    );
  }

  const requestTargets =
    availableHistory !== undefined &&
    availableHistory > 0 &&
    availableHistory < 4
      ? ([4] as const)
      : DASHBOARD_HISTORY_OPTIONS.filter(
          (history) =>
            availableHistory === undefined || history <= availableHistory,
        );

  return requestTargets.map((requestHistory) => {
    const displayCount =
      availableHistory !== undefined && availableHistory < requestHistory
        ? availableHistory
        : requestHistory;
    return {
      requestHistory,
      displayCount,
      label: dashboardHistoryLabel(validPeriod, displayCount),
    };
  });
}

const saoPauloISODate = (now: Date): string =>
  new Intl.DateTimeFormat("sv-SE", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);

const mondayOfISOWeek = (date: string): string => {
  const [year, month, day] = date.split("-").map(Number);
  const instant = new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1));
  const weekday = instant.getUTCDay();
  instant.setUTCDate(instant.getUTCDate() - (weekday === 0 ? 6 : weekday - 1));
  return instant.toISOString().slice(0, 10);
};

export function resolveDashboardPeriodRange(
  input: DashboardPeriodState,
  now: Date = new Date(),
): DashboardResolvedPeriodRange | undefined {
  const period = assertDashboardPeriod(input.period);
  assertDashboardHistory(input.history);

  if (
    period !== "custom" &&
    (input.from !== undefined || input.to !== undefined)
  ) {
    throw new TypeError("custom dates are valid only for the custom period");
  }
  if (period === "all") return undefined;
  if (period === "custom") {
    if (
      input.from === undefined ||
      input.to === undefined ||
      !isInclusiveISODateRange(input.from, input.to)
    ) {
      throw new TypeError(
        "custom dashboard period requires a valid inclusive date range",
      );
    }
    return { from: input.from, to: input.to };
  }

  const to = saoPauloISODate(now);
  if (period === "today") return { from: to, to };
  const [year = 0, month = 0] = to.split("-").map(Number);
  if (period === "week") {
    const from = mondayOfISOWeek(to);
    const end = new Date(`${from}T12:00:00Z`);
    end.setUTCDate(end.getUTCDate() + 6);
    return { from, to: end.toISOString().slice(0, 10) };
  }
  return {
    from: `${to.slice(0, 8)}01`,
    to: new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10),
  };
}

const compactISODate = (date: string): string =>
  `${date.slice(8, 10)}/${date.slice(5, 7)}`;

export function dashboardPeriodDisplayRange(
  input: DashboardPeriodState,
  now: Date = new Date(),
): string {
  if (input.period === "all") {
    resolveDashboardPeriodRange(input, now);
    return dashboardPeriodLabel("all");
  }

  const range = resolveDashboardPeriodRange(input, now);
  if (!range) throw new TypeError("dashboard period range is unavailable");
  if (input.period === "month") {
    return `${range.to.slice(5, 7)}/${range.to.slice(0, 4)}`;
  }
  if (range.from === range.to) return compactISODate(range.to);
  return `${compactISODate(range.from)} a ${compactISODate(range.to)}`;
}

export function dashboardHistoryLabel(
  period: DashboardPeriodKind,
  count: number,
): string {
  if (!Number.isSafeInteger(count) || count < 0) {
    throw new TypeError(
      "dashboard history count must be a nonnegative integer",
    );
  }
  const [singular, plural] =
    period === "today"
      ? ["dia", "dias"]
      : period === "week"
        ? ["semana", "semanas"]
        : period === "custom"
          ? ["período", "períodos"]
          : ["mês", "meses"];
  return `${count} ${count === 1 ? singular : plural}`;
}

export function serializeDashboardPeriod(input: DashboardPeriodState): string {
  const period = assertDashboardPeriod(input.period);
  const history = assertDashboardHistory(input.history);
  const params = new URLSearchParams({ period });

  if (period === "custom") {
    if (
      input.from === undefined ||
      input.to === undefined ||
      !isInclusiveISODateRange(input.from, input.to)
    ) {
      throw new TypeError(
        "custom dashboard period requires a valid inclusive date range",
      );
    }
    params.set("date_from", input.from);
    params.set("date_to", input.to);
  } else if (input.from !== undefined || input.to !== undefined) {
    throw new TypeError("custom dates are valid only for the custom period");
  }

  params.set("history", String(history));
  return params.toString();
}
